/**
 * Domain service that normalizes pipeline-step orders.
 *
 * Pipeline steps carry an `order` field that the UI may grow gaps in
 * (after deletions, drag-drop reshuffles, or hand-crafted inputs from the
 * persistence layer). The domain rule is that the persisted orders form a
 * contiguous zero-based sequence — possibly with ties to denote fused
 * steps, but with no gaps. This service owns that invariant.
 *
 * The service deliberately operates on raw numbers rather than on any
 * step shape: callers map the orders out of their own structures and apply
 * the result back. That keeps the rule reusable across both the UI state
 * layer (where it operates on draft DTOs) and the save command (where it
 * defends against untrusted input shapes).
 */
export class PipelineStepOrderService {
  /**
   * Normalizes a list of step orders so they form a contiguous, zero-based
   * sequence.
   *
   * Input is treated as positional: the i-th input maps to the i-th output.
   * Items sharing the same input order receive the same normalized order
   * (ties preserved). Items are NOT reordered — the output keeps the input
   * positional order. The mapping is based on the sorted unique values of
   * the input, so a sparser or out-of-order list still collapses to
   * `[0, 1, 2, ...]` ranks.
   *
   * Pure: never mutates the input array.
   *
   * @example
   *   normalize([]) // []
   *   normalize([0, 1, 2]) // [0, 1, 2]
   *   normalize([5, 3, 7, 1]) // [2, 1, 3, 0]
   *   normalize([5, 3, 5, 7, 1]) // [2, 1, 2, 3, 0]
   *   normalize([-1, 0, 5]) // [0, 1, 2]
   *   normalize([1.5, 0.5, 1.5]) // [1, 0, 1]
   */
  public static normalize(orders: number[]): number[] {
    if (orders.length === 0) return [];

    const uniqueSorted = Array.from(new Set(orders)).sort((a, b) => a - b);
    const rank = new Map<number, number>();
    uniqueSorted.forEach((value, index) => rank.set(value, index));

    return orders.map((value) => rank.get(value) as number);
  }
}
